/**
 * 文件路径: frontend/shared/api/deliveryTasks.ts
 * 版本: v1.0 | 创建日期: 2026-02-25 | 创建人: AI
 *
 * 配送任务 API 封装 - Phase 8 配送管理模块
 *
 * 端点:
 *   GET    delivery-tasks/                列表
 *   GET    delivery-tasks/{id}/           详情
 *   POST   delivery-tasks/{id}/assign/    分配配送员
 *   POST   delivery-tasks/{id}/depart/    确认出发
 *   POST   delivery-tasks/{id}/arrive/    确认到达
 *   POST   delivery-tasks/{id}/supplement/ 确认补货
 *   POST   delivery-tasks/{id}/complete/  确认完成
 *   POST   delivery-tasks/{id}/upload-proof/ 上传凭证
 *   POST   delivery-tasks/{id}/handle-late/  迟到处理
 */

import { httpClient } from './http'
import type {
  DeliveryTask,
  DeliveryTaskListParams,
  AssignDeliveryPersonRequest,
  UploadProofRequest,
  HandleLateRequest,
} from '../types/delivery'

/** 分页列表响应 */
interface PaginatedResponse<T> {
  total: number
  page: number
  page_size: number
  results: T[]
}

export const deliveryTasksApi = {
  /**
   * 查询配送任务列表（分页 + 过滤）
   *
   * @param params 查询参数（service_date / status / meal_segment / workplace 等）
   * @returns 分页配送任务列表
   */
  async list(params?: DeliveryTaskListParams): Promise<PaginatedResponse<DeliveryTask>> {
    const response = await httpClient.get<PaginatedResponse<DeliveryTask>>(
      'delivery-tasks/',
      { params },
    )
    return response
  },

  /**
   * 获取配送任务详情
   *
   * @param id 配送任务 ID
   * @returns 配送任务详情
   */
  async get(id: string | number): Promise<DeliveryTask> {
    const response = await httpClient.get<DeliveryTask>(
      `delivery-tasks/${id}/`,
    )
    return response
  },

  /**
   * 分配配送员
   *
   * @param id 配送任务 ID
   * @param data 配送员信息
   * @returns 更新后的配送任务
   */
  async assign(id: string | number, data: AssignDeliveryPersonRequest): Promise<DeliveryTask> {
    const response = await httpClient.post<DeliveryTask>(
      `delivery-tasks/${id}/assign/`,
      data,
    )
    return response
  },

  /**
   * 确认出发（配送员触发）
   *
   * @param id 配送任务 ID
   * @returns 更新后的配送任务（DEPARTED 状态）
   */
  async depart(id: string | number): Promise<DeliveryTask> {
    const response = await httpClient.post<DeliveryTask>(
      `delivery-tasks/${id}/depart/`,
    )
    return response
  },

  /**
   * 确认到达（配送员触发，自动计算 SLA 迟到）
   *
   * @param id 配送任务 ID
   * @returns 更新后的配送任务（ARRIVED 状态，含 is_late / late_duration_minutes）
   */
  async arrive(id: string | number): Promise<DeliveryTask> {
    const response = await httpClient.post<DeliveryTask>(
      `delivery-tasks/${id}/arrive/`,
    )
    return response
  },

  /**
   * 确认补货到达
   *
   * @param id 配送任务 ID
   * @returns 更新后的配送任务（SUPPLEMENTED 状态）
   */
  async supplement(id: string | number): Promise<DeliveryTask> {
    const response = await httpClient.post<DeliveryTask>(
      `delivery-tasks/${id}/supplement/`,
    )
    return response
  },

  /**
   * 确认完成（兜底：将剩余 PROCESSING 订单标记为 DELIVERED）
   *
   * @param id 配送任务 ID
   * @returns 更新后的配送任务（COMPLETED 状态）
   */
  async complete(id: string | number): Promise<DeliveryTask> {
    const response = await httpClient.post<DeliveryTask>(
      `delivery-tasks/${id}/complete/`,
    )
    return response
  },

  /**
   * 上传送达/补货凭证（JSON URL 模式，向后兼容）
   *
   * @param id 配送任务 ID
   * @param data 凭证信息 { images: [...], proof_type?: 'delivery' | 'supplement' }
   * @returns 更新后的配送任务
   */
  async uploadProof(id: string | number, data: UploadProofRequest): Promise<DeliveryTask> {
    const response = await httpClient.post<DeliveryTask>(
      `delivery-tasks/${id}/upload-proof/`,
      data,
    )
    return response
  },

  /**
   * 上传送达/补货凭证（文件上传模式）
   *
   * @param id 配送任务 ID
   * @param files 图片文件列表
   * @param proofType 凭证类型 'delivery' | 'supplement'
   * @returns 更新后的配送任务
   */
  async uploadProofFiles(
    id: string | number,
    files: File[],
    proofType: 'delivery' | 'supplement' = 'delivery',
  ): Promise<DeliveryTask> {
    const formData = new FormData()
    files.forEach(f => formData.append('images', f))
    formData.append('proof_type', proofType)
    const axiosInstance = httpClient.getAxiosInstance()
    const resp = await axiosInstance.post<{ data: DeliveryTask }>(
      `delivery-tasks/${id}/upload-proof/`,
      formData,
    )
    return resp.data.data
  },

  /**
   * 迟到处理（运营端）
   *
   * @param id 配送任务 ID
   * @param data 处理信息 { status: 'HANDLED' | 'WAIVED', notes?: string }
   * @returns 更新后的配送任务
   */
  async handleLate(id: string | number, data: HandleLateRequest): Promise<DeliveryTask> {
    const response = await httpClient.post<DeliveryTask>(
      `delivery-tasks/${id}/handle-late/`,
      data,
    )
    return response
  },

  // ==================== 配送码（H5 扫码方案） ====================

  /**
   * 签发配送码
   *
   * @param taskId 配送任务 ID
   * @returns 签发结果（含 token / h5_url / expires_at）
   */
  async issueToken(taskId: string | number): Promise<DeliveryTokenIssueResponse> {
    const response = await httpClient.post<DeliveryTokenIssueResponse>(
      `delivery-tokens/issue/`,
      { delivery_task_id: taskId },
    )
    return response
  },

  /**
   * 获取配送码二维码图片 URL（简单场景，浏览器自动携带 Cookie 认证）
   *
   * @param taskId 配送任务 ID
   * @returns 二维码图片 URL
   */
  getQRCodeUrl(taskId: string | number): string {
    return `/api/v1/delivery-tokens/${taskId}/qrcode/`
  },

  /**
   * 获取配送码二维码图片 Blob（需认证场景，携带 Authorization 头）
   *
   * @param taskId 配送任务 ID
   * @returns QR 码图片的 Blob 对象
   */
  async getQRCode(taskId: string | number): Promise<Blob> {
    const axiosInstance = httpClient.getAxiosInstance()
    const resp = await axiosInstance.get(`delivery-tokens/${taskId}/qrcode/`, {
      responseType: 'blob',
    })
    return resp.data
  },
}

/** 配送码签发响应 */
export interface DeliveryTokenIssueResponse {
  token: string
  h5_url: string
  expires_at: string
  task_id: number
}
